import { FocusTrap } from '@app/components/various/FocusTrap';
import { cn } from '@app/utils/cn';
import { Key } from '@app/utils/keys';
import React, { KeyboardEventHandler } from 'react';
import { Transition } from 'react-transition-group';

import { ModalSize } from '../Modal';
import { useModalContext } from '../ModalContext';

interface Props {
  contentLabel?: string;
  shouldReturnFocusAfterClose: boolean;
  size: ModalSize;
}

export const ModalContent = ({ children, contentLabel, shouldReturnFocusAfterClose, size }: React.WithChildren<Props>) => {
  const { containerRef, onClose, isOpen, shouldCloseOnEsc } = useModalContext();

  const onEscape: KeyboardEventHandler<HTMLDivElement> = event => {
    if (isOpen && event.code === Key.ESCAPE && shouldCloseOnEsc) {
      event.stopPropagation();
      onClose?.();
    }
  };

  const transitionStyles: Record<string, string> = {
    entered: 'opacity-100 scale-100',
    entering: 'opacity-100 scale-100',
    exited: 'opacity-0 scale-90',
    exiting: 'opacity-0 scale-90',
  };

  return (
    <Transition timeout={150} in={isOpen} appear unmountOnExit>
      {state => (
        <div
          ref={containerRef}
          aria-label={contentLabel}
          className={cn(
            'pointer-events-none fixed inset-0 z-modal flex items-end justify-center overflow-auto transition-[opacity,transform] md:items-center',
            transitionStyles[state],
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            className={cn(
              'pointer-events-auto relative flex max-h-[calc(100%-96px)] flex-col overflow-hidden rounded-t-regular bg-white md:max-h-[calc(100%-128px)] md:rounded-regular',
              {
                'w-[768px]': size === 'large',
                'w-full max-w-[1080px] md:w-[90%]': size === 'xlarge',
                'w-full md:w-[520px]': size === 'medium',
                'w-full md:w-[90%] 2xl:max-w-[1500px]': size === 'xxlarge',
              },
            )}
            tabIndex={-1}
            onKeyDown={onEscape}
          >
            <FocusTrap shouldReturnFocus={shouldReturnFocusAfterClose}>{children}</FocusTrap>
          </div>
        </div>
      )}
    </Transition>
  );
};
